'use server'

import { apiRequest, ApiResponse } from '../request'

export type MedicalSupplyType =
    | 'OPME'
    | 'PRODUCT'
    | 'DISPOSABLE_KIT'
    | 'INFECTION_PREVENTION_BUNDLE'
    | 'PACKAGE'
    | 'SURGICAL_MATERIAL'
    | 'INSTRUMENT'
    | 'IMPLANT'
    | 'EQUIPMENT'

export type NegotiationModality = 'SALE' | 'RENTAL'

export type ProductUnit =
    | 'UNIT'
    | 'KG'
    | 'GRAM'
    | 'LITER'
    | 'ML'
    | 'METER'
    | 'CM'
    | 'HOUR'
    | 'SESSION'
    | 'PACK'
    | 'BOX'
    | 'PAIR'
    | 'DOZEN'

export type ProductOrigin =
    | 'NATIONAL'
    | 'IMPORTED'
    | 'IMPORTED_NATIONAL_CONTENT'
    | 'NATIONAL_IMPORTED_CONTENT'

export const MEDICAL_SUPPLY_TYPE_LABELS: Record<MedicalSupplyType, string> = {
    OPME: 'OPME',
    PRODUCT: 'Produto',
    DISPOSABLE_KIT: 'Kit Descartável',
    INFECTION_PREVENTION_BUNDLE: 'Bundle Prevenção de Infecção',
    PACKAGE: 'Pacote',
    SURGICAL_MATERIAL: 'Material Cirúrgico',
    INSTRUMENT: 'Instrumental',
    IMPLANT: 'Implante',
    EQUIPMENT: 'Equipamento',
}

/**
 * Tipos que só existem na saúde: ficam fora das abas e do seletor de tipo para
 * segmentos não médicos.
 *
 * `OPME` entrou aqui depois: é sigla da ANS (Órteses, Próteses e Materiais
 * Especiais) e não significa nada num salão, numa academia ou numa oficina —
 * quem lê não sabe o que escolher. Implante e equipamento continuam visíveis:
 * um salão trabalha com implante capilar, uma oficina com equipamento alugado.
 *
 * ⚠️ No formulário, o tipo JÁ GRAVADO continua aparecendo (`t === type ||`).
 * Escondê-lo na edição deixaria o `select` apontando para um valor inexistente,
 * e o primeiro save trocaria o tipo do material sem ninguém pedir.
 */
export const HEALTH_ONLY_SUPPLY_TYPES: MedicalSupplyType[] = [
    'SURGICAL_MATERIAL',
    'INFECTION_PREVENTION_BUNDLE',
    'OPME',
]

export const NEGOTIATION_MODALITY_LABELS: Record<NegotiationModality, string> = {
    SALE: 'Venda',
    RENTAL: 'Aluguel',
}

export const PRODUCT_UNIT_LABELS: Record<ProductUnit, string> = {
    UNIT: 'Unidade',
    KG: 'Quilograma (kg)',
    GRAM: 'Grama (g)',
    LITER: 'Litro (L)',
    ML: 'Mililitro (mL)',
    METER: 'Metro (m)',
    CM: 'Centímetro (cm)',
    HOUR: 'Hora',
    SESSION: 'Sessão',
    PACK: 'Pacote',
    BOX: 'Caixa',
    PAIR: 'Par',
    DOZEN: 'Dúzia',
}

export const PRODUCT_ORIGIN_LABELS: Record<ProductOrigin, string> = {
    NATIONAL: 'Nacional',
    IMPORTED: 'Importado',
    IMPORTED_NATIONAL_CONTENT: 'Importado com Conteúdo Nacional',
    NATIONAL_IMPORTED_CONTENT: 'Nacional com Conteúdo Importado',
}

export interface MedicalSupply {
    id: number
    name: string
    description?: string | null
    type: MedicalSupplyType
    code?: string | null
    manufacturer?: string | null
    brand?: string | null
    anvisaCode?: string | null
    unit: ProductUnit
    quantity: number
    isHospital: boolean
    origin: ProductOrigin
    negotiationModality: NegotiationModality
    costPrice: number
    sellingPrice?: number | null
    markupPercentage?: number | null
    maxDiscount?: number | null
    supplierId?: number | null
    supplier?: { id: number; name: string; tradingName?: string; cnpj?: string } | null
    organizationId: number
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface CreateMedicalSupplyData {
    name: string
    type: MedicalSupplyType
    organizationId: number
    description?: string
    code?: string
    manufacturer?: string
    brand?: string
    anvisaCode?: string
    unit?: ProductUnit
    quantity?: number
    isHospital?: boolean
    origin?: ProductOrigin
    negotiationModality?: NegotiationModality
    costPrice: number
    sellingPrice?: number
    markupPercentage?: number
    maxDiscount?: number
    supplierId?: number | null
    active?: boolean
}

export interface UpdateMedicalSupplyData extends Partial<CreateMedicalSupplyData> {}

// ==================== CRUD ====================

export async function getMedicalSuppliesByOrganization(
    organizationId: number,
    params?: {
        activeOnly?: boolean
        page?: number
        limit?: number
        search?: string
        type?: MedicalSupplyType
        supplierId?: number
        isHospital?: boolean
        origin?: ProductOrigin
        negotiationModality?: NegotiationModality
        sortBy?: string
        sortOrder?: string
    }
): Promise<ApiResponse<MedicalSupply[]>> {
    const queryParams: Record<string, any> = {}
    if (params?.activeOnly) queryParams.activeOnly = 'true'
    if (params?.page) queryParams.page = params.page
    if (params?.limit) queryParams.limit = params.limit
    if (params?.search) queryParams.search = params.search
    if (params?.type) queryParams.type = params.type
    if (params?.supplierId) queryParams.supplierId = params.supplierId
    if (params?.isHospital != null) queryParams.isHospital = params.isHospital ? 'true' : 'false'
    if (params?.origin) queryParams.origin = params.origin
    if (params?.negotiationModality) queryParams.negotiationModality = params.negotiationModality
    if (params?.sortBy) queryParams.sortBy = params.sortBy
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder
    return apiRequest<MedicalSupply[]>(`medical-supply/organization/${organizationId}`, { params: queryParams })
}

export async function getMedicalSupply(id: number): Promise<ApiResponse<MedicalSupply>> {
    return apiRequest<MedicalSupply>(`medical-supply/${id}`, {})
}

export async function createMedicalSupply(data: CreateMedicalSupplyData): Promise<ApiResponse<MedicalSupply>> {
    return apiRequest<MedicalSupply>('medical-supply', { data }, 'post')
}

export async function updateMedicalSupply(id: number, data: UpdateMedicalSupplyData): Promise<ApiResponse<MedicalSupply>> {
    return apiRequest<MedicalSupply>(`medical-supply/${id}`, { data }, 'patch')
}

export async function toggleMedicalSupplyActive(id: number): Promise<ApiResponse<MedicalSupply>> {
    return apiRequest<MedicalSupply>(`medical-supply/${id}/toggle-active`, {}, 'patch')
}

export async function deleteMedicalSupply(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`medical-supply/${id}`, {}, 'delete')
}

export async function searchMedicalSupplies(
    organizationId: number,
    search?: string,
): Promise<ApiResponse<MedicalSupply[]>> {
    const queryParams: Record<string, any> = { activeOnly: 'true' }
    if (search) queryParams.search = search
    return apiRequest<MedicalSupply[]>(`medical-supply/organization/${organizationId}`, { params: queryParams })
}
